/**
 * 收件匣的所有「動作 + 面板狀態」：從舊 App.tsx 原封不動搬出來的邏輯層。
 *
 * 2026-10-27（T10）：這個檔從 1,339 行按**職責**拆成四支，這裡只剩「組裝」：
 * - `useSendActions`    送出／私訊／重送／送達確認／帳號已死
 * - `useIdentityActions` 開通、切換、健康看板的重連與刪除、重建失效身分
 * - `useBatchActions`   批次進房／全部離開＋帳號池（逐顆操作一組帳號）
 * - `useNoticeQueue`    通知佇列（誰都可以推，只有它在管）
 * 加上留在本檔的「清單、導覽、抽屜、熱鍵、房間」——這些是版面本身，不屬於任何一支動作。
 *
 * 界線（不變）：
 * - **不碰 `useInbox`**。它由 `App` 在最外層呼叫一次並把回傳值傳進來；這個 hook 只消費。
 *   理由：WS 訂閱只能有一份，而且必須活得比任何外觀元件久（見 PLAN 的 R3）。
 *   所以這裡沒有 `useChatApi`、也沒有第二個 `client.subscribe()`。
 * - 只回資料與 callback，不組 JSX。畫面在 `shell/` 底下。
 * - 對外介面**沒有縮**：`ConsoleActions` 的欄位一個都沒少，型別從本檔 re-export，
 *   呼叫端連 import 路徑都不必改。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import {
  sortConversationsByHighlight,
  useHighlights,
  type Conversation,
  type RoomInfo,
  type Service,
  type UseInboxResult,
} from '@chat-rea/console-ui';
import { client } from '../api';
import {
  DEFAULT_ROOM,
  DEFAULT_SERVICE,
  type ConsoleActions,
  type DrawerTab,
} from './consoleActions.shared';
import { useNoticeQueue } from './useNoticeQueue';
import { useSendActions, type LiveRefValue } from './useSendActions';
import { useIdentityActions } from './useIdentityActions';
import { useBatchActions } from './useBatchActions';

// 對外介面原樣 re-export：呼叫端（App、LeftRail、RoomSidebar、InboxLayout、AccountsPage）
// 的 import 路徑與型別名稱都不動。
export type {
  ConsoleActions,
  DrawerTab,
  LeaveAllFailure,
  LeaveAllProgress,
  LeaveAllResult,
  OpeningAttempt,
  OpeningState,
} from './consoleActions.shared';
export { LEAVE_GAP_MS } from './consoleActions.shared';

function errMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

export function useConsoleActions(inbox: UseInboxResult, narrow: boolean): ConsoleActions {
  const { conversations: inboxConversations, active, activeId, activeEvents } = inbox;
  const { select, markRead, refresh, dismissDropped, refreshEvents } = inbox;
  const dropped = inbox.dropped;

  /**
   * 關鍵字提醒：暱稱自動加入（有人喊你就該看到），自訂字存 localStorage。
   * 排序在這裡做——命中的**未讀**對話置頂，其餘照 useInbox 原本的規則。
   */
  const highlights = useHighlights({
    nicknames: inboxConversations.map((item) => item.nickname),
  });
  const { isHit: isHighlighted, keywords: highlightKeywords } = highlights;
  const conversations = useMemo(
    () =>
      sortConversationsByHighlight(inboxConversations, (item) =>
        isHighlighted(item.lastMessagePreview),
      ),
    [inboxConversations, isHighlighted],
  );

  /** 自訂關鍵字的輸入草稿（還沒按 Enter／加入）。 */
  const [keywordDraft, setKeywordDraft] = useState('');

  /** 桌面右側抽屜開關；手機是分頁，不用這個。 */
  const [drawerOpen, setDrawerOpen] = useState(true);
  /** 右側抽屜分頁：房間／帳號健康／帳號池。 */
  const [drawerTab, setDrawerTab] = useState<DrawerTab>('rooms');
  /** 手機「對話」分頁：false＝看清單，true＝看對話串（返回鍵回清單）。 */
  const [mobileThread, setMobileThread] = useState(false);

  const [rooms, setRooms] = useState<RoomInfo[]>([]);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [roomsError, setRoomsError] = useState<string | null>(null);
  /** 右欄點過的房間；沒有目前房間時就當作建帳號的目的地。 */
  const [pickedRoom, setPickedRoom] = useState<string>(DEFAULT_ROOM);

  const [busy, setBusy] = useState(false);

  /** 通知佇列（T09 立介面，T10 搬進 `useNoticeQueue`）。 */
  const noticeQueue = useNoticeQueue();
  const { setActionError, setNotice, push } = noticeQueue;

  const roomsReqRef = useRef(0);

  /**
   * 會傳進 memo 子元件的回呼（say／sendPm／retryOutgoing）不能在 deps 裡放 `conversations`／
   * `activeEvents`／`active` 這些「每次訊息進來就換身分」的值：
   * undefined `active` 一換，`say` 就換，`retryOutgoing` 跟著換，`ThreadRow` 的 memo 對整個
   * 對話串（實測 34～170 列）全部失效——每次 WS 訊息、每次送出都重算整串，這是 80–160ms
   * 長工作的來源。改成呼叫當下從 ref 讀最新值，回呼本身保持同一個參考。
   */
  const liveRef = useRef<LiveRefValue>({ active, activeEvents, conversations });
  liveRef.current = { active, activeEvents, conversations };

  const currentRoom = active?.room ?? null;
  const currentService = active?.service ?? DEFAULT_SERVICE;
  const targetRoom = currentRoom ?? pickedRoom ?? DEFAULT_ROOM;

  const loadRooms = useCallback(async (service: Service) => {
    const token = (roomsReqRef.current += 1);
    setRoomsLoading(true);
    try {
      const list = await client.listRooms(service);
      if (token !== roomsReqRef.current) return;
      setRooms(list);
      setRoomsError(null);
    } catch (err) {
      if (token !== roomsReqRef.current) return;
      setRooms([]);
      setRoomsError(errMessage(err));
    } finally {
      if (token === roomsReqRef.current) setRoomsLoading(false);
    }
  }, []);

  // 切帳號（換 service）就重抓該 service 的房間；失敗留在右欄顯示＋可重試。
  useEffect(() => {
    void loadRooms(currentService);
  }, [currentService, loadRooms]);

  /**
   * #02：清單載入失敗也走同一條佇列（不再是「一條橫幅被對話錯誤或動作錯誤擠掉」）。
   * 連不上＝warning（舊資料還能看），未授權／其他＝error（要人處理）；都帶「重試」。
   */
  useEffect(() => {
    if (!inbox.error) return;
    const offline = inbox.errorKind === 'offline';
    push({
      kind: offline ? 'warning' : 'error',
      message: inbox.error,
      detail: offline ? '清單顯示的是最後一次成功抓到的內容；恢復後會自己更新。' : null,
      retry: () => void refresh(),
    });
  }, [inbox.error, inbox.errorKind, push, refresh]);

  // 四支動作 hook：送出 → 身分（要 markGone）→ 批次／帳號池（要 runOpening）。
  const send = useSendActions({
    inbox,
    liveRef,
    targetRoom,
    setBusy,
    setActionError,
    setNotice,
    clearErrorNotices: noticeQueue.clearKind,
  });
  const identity = useIdentityActions({
    inbox,
    conversations,
    currentService,
    targetRoom,
    setBusy,
    setMobileThread,
    setActionError,
    setNotice,
    markGone: send.markGone,
  });
  const batch = useBatchActions({
    inbox,
    conversations,
    activeId,
    currentService,
    targetRoom,
    setBusy,
    setActionError,
    setNotice,
    pushNotice: push,
    runOpening: identity.runOpening,
    openingError: () => identity.lastOpeningErrorRef.current,
  });
  const { openingRef, dismissOpening, openConversation, jumpToUnread } = identity;
  const { markGone } = send;

  /** 熱鍵（只在輸入框外生效）：Alt/Cmd/Ctrl+1..9 切對話、Tab 跳未讀、/ 聚焦指令列、Esc 關抽屜。純數字不觸發。 */
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // 中文輸入法組字中的按鍵是在選字，不要搶。
      if (event.isComposing) return;

      const el = document.activeElement as HTMLElement | null;
      const tag = el?.tagName ?? '';
      const typing = tag === 'TEXTAREA' || tag === 'INPUT' || el?.isContentEditable === true;
      const withModifier = event.altKey || event.ctrlKey || event.metaKey;

      // 輸入框（含 contenteditable）有焦點時，**一律不觸發任何熱鍵**：
      // 打字打到一半按到數字、Tab、/ 或 Esc 都不該切頁或搶焦點。
      if (typing) return;

      // 1–5 切帳號：**只有一種**觸發方式＝Alt/Ctrl/Cmd+數字。
      // 純數字不切頁（以前兩種並存，打數字會誤切對話）。
      if (/^[1-9]$/.test(event.key) && withModifier) {
        const index = Number(event.key) - 1;
        const target = conversations[index];
        if (!target) return;
        event.preventDefault();
        openConversation(target.id);
        return;
      }

      // Tab：有未讀就跳到下一列未讀並把焦點移過去；沒有未讀時**不攔**，
      // 讓瀏覽器原生 Tab 走焦點順序（否則鍵盤使用者會被鎖在目前這個元素）。
      // Shift+Tab 一律放行（反向移動不能被吃掉）；在輸入框裡 Tab 是「離開這個欄位」，也不劫持。
      if (event.key === 'Tab') {
        if (event.shiftKey || withModifier || typing) return;
        if (jumpToUnread()) event.preventDefault();
        return;
      }

      if (event.key === '/' && !withModifier && !typing) {
        const input = document.querySelector<HTMLTextAreaElement>('[data-testid="command-input"]');
        if (!input) return;
        event.preventDefault();
        if (input.disabled) {
          // 不要靜默失敗：說清楚為什麼 `/` 沒把游標送過去。
          setActionError('指令列目前不能打字（還沒選身分）：先在左邊選一個對話，再按 /。');
          return;
        }
        input.focus();
        return;
      }

      if (event.key === 'Escape' && !typing) {
        // 失敗的開通面板優先關：它壓在內容上方，不關掉等於把鍵盤鎖在那一區。
        if (openingRef.current?.status === 'failed') {
          dismissOpening();
          return;
        }
        if (drawerOpen && !narrow) setDrawerOpen(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    conversations,
    dismissOpening,
    drawerOpen,
    jumpToUnread,
    narrow,
    openConversation,
    openingRef,
    setActionError,
  ]);

  /** 點房間＝把目前帳號搬過去（沒帳號時只記為建帳號的目的地）。 */
  const joinRoom = useCallback(
    (room: RoomInfo) => {
      const { active: current } = liveRef.current;
      setPickedRoom(room.id);
      if (!current) {
        setNotice(`已選 ${room.id}；按「＋ 建帳號」就會進這間`);
        return;
      }
      const accountId = current.id;
      void (async () => {
        setBusy(true);
        setActionError(null);
        try {
          await client.joinRoom(accountId, room.id);
          await refresh();
        } catch (err) {
          if (!markGone(err, accountId)) setActionError(errMessage(err));
        } finally {
          setBusy(false);
        }
      })();
    },
    [markGone, refresh, setActionError, setNotice],
  );

  const submitKeyword = useCallback(() => {
    const added = highlights.add(keywordDraft);
    if (added) setKeywordDraft('');
  }, [highlights, keywordDraft]);

  const unreadTotal = useMemo(
    () => conversations.reduce((sum, item) => sum + item.unread, 0),
    [conversations],
  );

  return {
    conversations,
    unreadTotal,
    activeId,
    active,
    activeEvents,
    eventsLoading: inbox.eventsLoading,
    inboxLoading: inbox.loading,
    inboxError: inbox.error,
    inboxErrorKind: inbox.errorKind,
    inboxLoaded: inbox.conversationsLoaded,
    eventsError: inbox.eventsError,
    eventsErrorKind: inbox.eventsErrorKind,
    refreshEvents,
    dropped,
    notices: noticeQueue.notices,
    dismissNotice: noticeQueue.dismiss,
    clearErrorNotices: noticeQueue.clearKind,
    noticeError: noticeQueue.hasError,
    openConversation,
    dismissGone: dismissDropped,
    refresh,
    highlights,
    highlightKeywords,
    keywordDraft,
    setKeywordDraft,
    submitKeyword,
    drawerOpen,
    setDrawerOpen,
    drawerTab,
    setDrawerTab,
    mobileThread,
    setMobileThread,
    rooms,
    roomsLoading,
    roomsError,
    currentRoom,
    currentService,
    targetRoom,
    loadRooms,
    joinRoom,
    busy,
    say: send.say,
    sendPm: send.sendPm,
    retryOutgoing: send.retryOutgoing,
    verifyDelivery: send.verifyDelivery,
    createAccount: identity.createAccount,
    createError: identity.createError,
    opening: identity.opening,
    retryOpening: identity.retryOpening,
    dismissOpening: identity.dismissOpening,
    reconnectAccount: identity.reconnectAccount,
    deleteAccount: identity.deleteAccount,
    rebuildDropped: identity.rebuildDropped,
    sessionPicked: batch.sessionPicked,
    toggleSessionPick: batch.toggleSessionPick,
    joinSessions: batch.joinSessions,
    leaveAll: batch.leaveAll,
    leaveAllProgress: batch.leaveAllProgress,
    leaveAllResult: batch.leaveAllResult,
    poolAccounts: batch.poolAccounts,
    poolLoading: batch.poolLoading,
    poolError: batch.poolError,
    poolRegistering: batch.poolRegistering,
    poolResults: batch.poolResults,
    poolRegisterError: batch.poolRegisterError,
    poolCooldownUntil: batch.poolCooldownUntil,
    poolFilter: batch.poolFilter,
    setPoolFilter: batch.setPoolFilter,
    poolRegisterService: batch.poolRegisterService,
    setPoolRegisterService: batch.setPoolRegisterService,
    loadPool: batch.loadPool,
    registerPool: batch.registerPool,
    usePoolAccount: batch.usePoolAccount,
    deletePoolAccount: batch.deletePoolAccount,
    renameAccount: batch.renameAccount,
  };
}
